/**
 * Performance Aggregation Service
 *
 * Collects and aggregates performance metrics from all users into Firestore
 * for display in the Admin Performance Dashboard.
 *
 * Data Flow:
 * 1. Client-side: Web Vitals + custom traces collected
 * 2. This service: Aggregates and stores to Firestore `performanceMetrics` collection
 * 3. Admin Dashboard: Reads aggregated data for visualization
 */

import {
  unifiedSetDoc,
  unifiedGetDoc,
  unifiedUpdateDoc,
  getDb,
  getFirebaseAuth,
  isFirebaseInitialized,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from '@watchlist/shared';
import { isNative } from '../capacitor-init';

/**
 * Normalize Firestore DocumentSnapshot vs native unified adapter.
 * Web SDK exposes `exists` / `data` as methods — they must be called with `.call(snap)` or `this` is lost
 * (throws: Cannot read properties of undefined (reading '_document')).
 */
function unifiedSnapshotExists(snap: unknown): boolean {
  if (snap == null || typeof snap !== 'object') return false;
  try {
    const ex = (snap as Record<string, unknown>).exists;
    if (typeof ex === 'function') {
      return !!(ex as (this: unknown) => boolean).call(snap);
    }
    return !!ex;
  } catch {
    return false;
  }
}

function unifiedSnapshotData<T extends Record<string, unknown>>(snap: unknown): T | undefined {
  if (!unifiedSnapshotExists(snap)) return undefined;
  try {
    const d = (snap as Record<string, unknown>).data;
    if (typeof d === 'function') {
      return (d as (this: unknown) => T).call(snap) as T;
    }
    return d as T;
  } catch {
    return undefined;
  }
}

function canWritePerformanceTelemetry(): boolean {
  if (!isFirebaseInitialized()) {
    return false;
  }
  try {
    return !!getFirebaseAuth().currentUser;
  } catch {
    return false;
  }
}

// Types for performance data
export interface WebVitalMetric {
  name: 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: string;
}

export interface PageLoadMetric {
  pageName: string;
  loadTimeMs: number;
  /** Omit when cache status was not measured (do not count as a miss). */
  cacheHit?: boolean;
  timestamp: string;
  attributes?: Record<string, string>;
}

export interface AggregatedMetrics {
  date: string; // YYYY-MM-DD
  webVitals: {
    [key: string]: {
      count: number;
      sum: number;
      avg: number;
      p75: number;
      p95: number;
      goodCount: number;
      needsImprovementCount: number;
      poorCount: number;
    };
  };
  pageLoads: {
    [pageName: string]: {
      count: number;
      totalTimeMs: number;
      avgTimeMs: number;
      cacheHitCount: number;
      /** Samples where cacheHit was explicitly true or false (excludes "unknown" / omitted). */
      cacheKnownCount?: number;
      cacheHitRate: number;
    };
  };
  userCount: number;
  sessionCount: number;
  updatedAt: string;
}

/** One calendar day in the admin history window (may have no Firestore doc yet). */
export interface HistoryDaySlot {
  date: string;
  metrics: AggregatedMetrics | null;
}

export interface PerformanceSummary {
  today: AggregatedMetrics | null;
  yesterday: AggregatedMetrics | null;
  last7Days: AggregatedMetrics[];
  /** Last 30 days, oldest → newest, for trend charts. */
  historySlots: HistoryDaySlot[];
  realtimeMetrics: WebVitalMetric[];
}

const HISTORY_CHART_DAYS = 30;

// Collection names
const METRICS_COLLECTION = 'performanceMetrics';
const REALTIME_COLLECTION = 'performanceRealtime';

/**
 * Get today's date in YYYY-MM-DD format
 */
const getTodayKey = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get yesterday's date in YYYY-MM-DD format
 */
const getYesterdayKey = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

/**
 * Report a Web Vital metric to Firestore for aggregation
 * This is called from performanceMonitoring.ts
 */
export const reportWebVitalToFirestore = async (metric: WebVitalMetric): Promise<void> => {
  // Skip on native - already logged to Analytics
  if (isNative()) return;
  // Firestore rules require auth; Web Vitals can fire before login — skip until signed in
  if (!canWritePerformanceTelemetry()) return;

  try {
    const dateKey = getTodayKey();
    const docId = `daily_${dateKey}`;

    // Try to update existing document
    const existingDoc = await unifiedGetDoc(METRICS_COLLECTION, docId);

    if (unifiedSnapshotExists(existingDoc)) {
      const data = unifiedSnapshotData<AggregatedMetrics>(existingDoc);
      if (!data) return;
      const vitalData = data.webVitals[metric.name] || {
        count: 0,
        sum: 0,
        avg: 0,
        p75: 0,
        p95: 0,
        goodCount: 0,
        needsImprovementCount: 0,
        poorCount: 0,
      };

      // Update aggregations
      vitalData.count += 1;
      vitalData.sum += metric.value;
      vitalData.avg = vitalData.sum / vitalData.count;

      // Update rating counts
      if (metric.rating === 'good') vitalData.goodCount += 1;
      else if (metric.rating === 'needs-improvement') vitalData.needsImprovementCount += 1;
      else vitalData.poorCount += 1;

      const uid = getFirebaseAuth().currentUser?.uid;
      await unifiedUpdateDoc(METRICS_COLLECTION, docId, {
        [`webVitals.${metric.name}`]: vitalData,
        updatedAt: new Date().toISOString(),
        ...(uid ? { reporterUid: uid } : {}),
      });
    } else {
      // Create new document for today
      const newData: AggregatedMetrics = {
        date: dateKey,
        webVitals: {
          [metric.name]: {
            count: 1,
            sum: metric.value,
            avg: metric.value,
            p75: metric.value,
            p95: metric.value,
            goodCount: metric.rating === 'good' ? 1 : 0,
            needsImprovementCount: metric.rating === 'needs-improvement' ? 1 : 0,
            poorCount: metric.rating === 'poor' ? 1 : 0,
          },
        },
        pageLoads: {},
        userCount: 1,
        sessionCount: 1,
        updatedAt: new Date().toISOString(),
      };

      const uid = getFirebaseAuth().currentUser?.uid;
      await unifiedSetDoc(
        METRICS_COLLECTION,
        docId,
        uid ? { ...newData, reporterUid: uid } : newData,
        false
      );
    }

    // Also store to realtime collection for live dashboard (last 100 entries)
    const realtimeId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const rtUid = getFirebaseAuth().currentUser?.uid;
    await unifiedSetDoc(
      REALTIME_COLLECTION,
      realtimeId,
      {
        ...metric,
        timestamp: new Date().toISOString(),
        ...(rtUid ? { reporterUid: rtUid } : {}),
      },
      false
    );
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    const isPermission =
      msg.includes('permission') || msg.includes('Permission') || msg.includes('insufficient');
    if (isPermission) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Web Vitals: Firestore aggregation skipped (rules or permissions).');
      }
      return;
    }
    console.error('Failed to report Web Vital to Firestore:', error);
  }
};

/**
 * Report a page load metric to Firestore for aggregation
 */
export const reportPageLoadToFirestore = async (metric: PageLoadMetric): Promise<void> => {
  if (isNative()) return;
  if (!canWritePerformanceTelemetry()) return;

  try {
    const dateKey = getTodayKey();
    const docId = `daily_${dateKey}`;

    const existingDoc = await unifiedGetDoc(METRICS_COLLECTION, docId);

    if (unifiedSnapshotExists(existingDoc)) {
      const data = unifiedSnapshotData<AggregatedMetrics>(existingDoc);
      if (!data) return;
      const pageData = data.pageLoads[metric.pageName] || {
        count: 0,
        totalTimeMs: 0,
        avgTimeMs: 0,
        cacheHitCount: 0,
        cacheKnownCount: 0,
        cacheHitRate: 0,
      };

      pageData.count += 1;
      pageData.totalTimeMs += metric.loadTimeMs;
      pageData.avgTimeMs = pageData.totalTimeMs / pageData.count;

      if (typeof metric.cacheHit === 'boolean') {
        pageData.cacheKnownCount = (pageData.cacheKnownCount ?? 0) + 1;
        if (metric.cacheHit) pageData.cacheHitCount += 1;
        pageData.cacheHitRate =
          pageData.cacheKnownCount > 0 ? pageData.cacheHitCount / pageData.cacheKnownCount : 0;
      } else {
        const known = pageData.cacheKnownCount ?? 0;
        pageData.cacheHitRate = known > 0 ? pageData.cacheHitCount / known : 0;
      }

      const plUid = getFirebaseAuth().currentUser?.uid;
      await unifiedUpdateDoc(METRICS_COLLECTION, docId, {
        [`pageLoads.${metric.pageName}`]: pageData,
        updatedAt: new Date().toISOString(),
        ...(plUid ? { reporterUid: plUid } : {}),
      });
    } else {
      const newData: AggregatedMetrics = {
        date: dateKey,
        webVitals: {},
        pageLoads: {
          [metric.pageName]: {
            count: 1,
            totalTimeMs: metric.loadTimeMs,
            avgTimeMs: metric.loadTimeMs,
            cacheHitCount: metric.cacheHit === true ? 1 : 0,
            cacheKnownCount: typeof metric.cacheHit === 'boolean' ? 1 : 0,
            cacheHitRate:
              typeof metric.cacheHit === 'boolean'
                ? metric.cacheHit
                  ? 1
                  : 0
                : 0,
          },
        },
        userCount: 1,
        sessionCount: 1,
        updatedAt: new Date().toISOString(),
      };

      const plCreateUid = getFirebaseAuth().currentUser?.uid;
      await unifiedSetDoc(
        METRICS_COLLECTION,
        docId,
        plCreateUid ? { ...newData, reporterUid: plCreateUid } : newData,
        false
      );
    }
  } catch (error) {
    console.error('Failed to report page load to Firestore:', error);
  }
};

/**
 * Get aggregated metrics for the admin dashboard
 */
export const getPerformanceSummary = async (): Promise<PerformanceSummary> => {
  try {
    const todayKey = getTodayKey();
    const yesterdayKey = getYesterdayKey();

    // Get today's metrics
    const todayDoc = await unifiedGetDoc(METRICS_COLLECTION, `daily_${todayKey}`);
    const today = unifiedSnapshotData<AggregatedMetrics>(todayDoc) ?? null;

    // Get yesterday's metrics
    const yesterdayDoc = await unifiedGetDoc(METRICS_COLLECTION, `daily_${yesterdayKey}`);
    const yesterday = unifiedSnapshotData<AggregatedMetrics>(yesterdayDoc) ?? null;

    // Get last 7 days (newest first: today, yesterday, …)
    const last7Days: AggregatedMetrics[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      const doc = await unifiedGetDoc(METRICS_COLLECTION, `daily_${dateKey}`);
      const dayData = unifiedSnapshotData<AggregatedMetrics>(doc);
      if (dayData) {
        last7Days.push(dayData);
      }
    }

    // Last 30 days for charts (oldest → newest), fetched in parallel
    const historyOffsets = Array.from({ length: HISTORY_CHART_DAYS }, (_, idx) => HISTORY_CHART_DAYS - 1 - idx);
    const historyDateKeys = historyOffsets.map((off) => {
      const d = new Date();
      d.setDate(d.getDate() - off);
      return d.toISOString().split('T')[0];
    });
    const historySnapshots = await Promise.all(
      historyDateKeys.map((key) => unifiedGetDoc(METRICS_COLLECTION, `daily_${key}`))
    );
    const historySlots: HistoryDaySlot[] = historyDateKeys.map((date, i) => ({
      date,
      metrics: unifiedSnapshotData<AggregatedMetrics>(historySnapshots[i]) ?? null,
    }));

    // Get realtime metrics (last 50)
    const realtimeMetrics: WebVitalMetric[] = [];
    try {
      const db = getDb();
      const realtimeRef = collection(db, REALTIME_COLLECTION);
      const realtimeQuery = query(
        realtimeRef,
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const realtimeSnapshot = await getDocs(realtimeQuery);
      realtimeSnapshot.forEach((doc) => {
        realtimeMetrics.push(doc.data() as WebVitalMetric);
      });
    } catch (err) {
      console.error('Error fetching realtime metrics:', err);
    }

    return {
      today,
      yesterday,
      last7Days,
      historySlots,
      realtimeMetrics,
    };
  } catch (error) {
    console.error('Error getting performance summary:', error);
    return {
      today: null,
      yesterday: null,
      last7Days: [],
      historySlots: [],
      realtimeMetrics: [],
    };
  }
};

/**
 * Clean up old realtime metrics (keep only last 24 hours)
 * Call this periodically or via Cloud Function
 */
export const cleanupOldRealtimeMetrics = async (): Promise<number> => {
  try {
    const db = getDb();
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    const realtimeRef = collection(db, REALTIME_COLLECTION);
    const oldMetricsQuery = query(
      realtimeRef,
      where('timestamp', '<', oneDayAgo),
      limit(100) // Batch delete
    );

    const snapshot = await getDocs(oldMetricsQuery);
    let deletedCount = 0;

    // Note: For actual deletion, you'd use batch writes
    // This is a simplified version
    console.log(`Found ${snapshot.size} old realtime metrics to cleanup`);
    deletedCount = snapshot.size;

    return deletedCount;
  } catch (error) {
    console.error('Error cleaning up old metrics:', error);
    return 0;
  }
};
